/**
 * SubscriptionSection — Plan display, trial info, manage/upgrade button
 */

import React from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Pressable,
  Button,
  ButtonText,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { FS_SM, FS_MD } from '../../theme';
import { SectionCard } from '../ui';
import { useHasProAccess, useIsInTrial, useTrialDaysLeft } from '../../store/subscriptionStore';

export const SubscriptionSection = React.memo(function SubscriptionSection() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const navigation = useNavigation('Profile');

  const hasProAccess = useHasProAccess();
  const isInTrial = useIsInTrial();
  const trialDaysLeft = useTrialDaysLeft();

  return (
    <SectionCard>
      <HStack justifyContent="space-between" alignItems="center">
        <Text allowFontScaling={false} accessibilityRole="header" style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.text, ...ffs }}>
          {t('profile.subscription.title')}
        </Text>
        <Pressable testID="profile-button-subscription" accessibilityLabel={hasProAccess && !isInTrial ? t('profile.subscription.manageAccessibility') : t('profile.subscription.upgradeAccessibility')} accessibilityRole="button" onPress={() => navigation.navigate('Paywall')}>
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}>
            {hasProAccess && !isInTrial ? t('common.manage') : t('common.upgrade')}
          </Text>
        </Pressable>
      </HStack>

      <Box
        // Plan-state testID so E2E can assert which plan badge is shown
        // (the badge text varies by plan but the variant is otherwise
        // unaddressable). pro / trial / free are mutually exclusive.
        testID={
          hasProAccess && !isInTrial
            ? 'profile-subscription-pro'
            : isInTrial
              ? 'profile-subscription-trial'
              : 'profile-subscription-free'
        }
        style={{
          backgroundColor: hasProAccess ? theme.successBg : isInTrial ? theme.warningBg : theme.inputBg,
          borderWidth: hasProAccess || isInTrial ? 1 : 0,
          borderColor: hasProAccess ? theme.success : isInTrial ? theme.warning : 'transparent',
        }}
        p="$4"
        rounded="$lg"
      >
        <VStack space="xs">
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.text, ...ffs }}
          >
            {hasProAccess && !isInTrial
              ? t('profile.subscription.pro')
              : isInTrial
              ? t('profile.subscription.freeTrial')
              : t('profile.subscription.freePlan')}
          </Text>
          {isInTrial && trialDaysLeft !== null && (
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
              {t('profile.subscription.daysRemaining', { count: trialDaysLeft })}
            </Text>
          )}
          {!hasProAccess && !isInTrial && (
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
              {t('profile.subscription.upgradeMessage')}
            </Text>
          )}
          {!hasProAccess && (
            <Button
              size="md"
              style={{ backgroundColor: theme.primary, marginTop: 12 }}
              onPress={() => navigation.navigate('Paywall')}
            >
              <ButtonText allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: 'white', ...ffs }}>
                {isInTrial ? t('common.subscribe') : t('common.upgrade')}
              </ButtonText>
            </Button>
          )}
        </VStack>
      </Box>
    </SectionCard>
  );
});
